import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, ArrowLeft } from "lucide-react";
import { setInitialPassword } from "@/services/auth";

interface SetPasswordSectionProps {
  onComplete: () => void;
  onSkip?: () => void;
  onBack?: () => void;
}

export default function SetPasswordSection({
  onComplete,
  onSkip,
  onBack,
}: SetPasswordSectionProps) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (password.length <= 10) {
      newErrors.password = "Password must be more than 10 characters";
    }

    if (password !== confirmPassword) {
      newErrors.confirm = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSaving(true);
      await setInitialPassword(password);
      toast.success("Password set! You can now log in with your email and password.");
      onComplete();
    } catch (err: any) {
      toast.error(String(err?.message || "Failed to set password"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-5">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2"
        >
          <ArrowLeft className="h-3 w-3" />
          <span>Back</span>
        </button>
      )}
      <div className="flex items-center gap-2 mb-1">
        <Lock className="h-4 w-4 text-primary-text shrink-0" />
        <h3 className="text-sm font-semibold text-foreground">Set a password</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Set a password so you can log in directly next time without waiting for an email link.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Password</label>
          <Input
            type="password"
            placeholder="At least 11 characters"
            value={password}
            onChange={e => {
              setPassword(e.target.value);
              setErrors(p => ({ ...p, password: undefined }));
            }}
          />
          {errors.password && <p className="text-xs text-destructive mt-1">{errors.password}</p>}
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">
            Confirm password
          </label>
          <Input
            type="password"
            placeholder="Re-enter your password"
            value={confirmPassword}
            onChange={e => {
              setConfirmPassword(e.target.value);
              setErrors(p => ({ ...p, confirm: undefined }));
            }}
          />
          {errors.confirm && <p className="text-xs text-destructive mt-1">{errors.confirm}</p>}
        </div>
      </div>
      <div className="flex items-center justify-between mt-4">
        {onSkip ? (
          <button
            onClick={onSkip}
            className="text-xs text-muted-foreground hover:text-foreground hover:underline transition-colors"
          >
            Skip for now
          </button>
        ) : (
          <div />
        )}
        <Button onClick={handleSubmit} disabled={saving} size="sm">
          {saving ? "Setting password..." : "Set Password"}
        </Button>
      </div>
    </div>
  );
}
